/**
 * Two formatters, because the app holds two kinds of time-ish value and they
 * do not behave alike.
 */

/**
 * A calendar date — `weddingDate` and anything else backed by a Postgres
 * `date` column. It arrives as `2027-06-12`: no time, no zone, nothing to
 * convert. A wedding on the 12th is on the 12th for a planner in Sydney and
 * one in Los Angeles alike.
 *
 * The parts are read off the string and handed to `Date.UTC` rather than
 * passed through `new Date(...)`, so no local-midnight parse can pull the day
 * backwards. The `timeZone: 'UTC'` below pairs with that construction and is
 * not a claim that the date means anything in UTC — it means nothing anywhere.
 */
export function formatDate(
  value: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string {
  if (!value) return '—';
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '—';
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' }).format(date);
}

/**
 * The same calendar date, a whole number of days on.
 *
 * Built the way `formatDate` is: the parts are read off the string and handed
 * to `Date.UTC`, so no local-midnight parse can pull the day backwards, and
 * the result is spelled the way a date column arrives. Month lengths and leap
 * years are `Date`'s problem, which it is reliable at and a hand-rolled
 * calculation is not.
 *
 * Null, not the em dash `formatDate` renders, when there is no date to shift:
 * a caller wants a date it can go on to format, and "—" shifted by a day is
 * not one.
 */
export function shiftDate(value: string | null | undefined, days: number): string | null {
  if (!value) return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * An instant — `sentAt`, `lastSyncedAt`, `scheduledSendAt` and friends: a
 * moment that actually happened, stored UTC and rendered in the viewer's own
 * zone. "Last synced 4:12 PM" is only reassuring if it means 4:12 PM where the
 * planner is sitting, and formatting an instant in UTC would report a sync
 * that just happened as seven hours from now.
 */
export function formatInstant(
  iso: string | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  },
): string {
  if (!iso) return '—';
  const date = iso instanceof Date ? iso : new Date(iso);
  return new Intl.DateTimeFormat('en-US', opts).format(date);
}
